import type { MessageRow, UserAuth } from '@repo/common';
import type { ExchangeMessage } from '@repo/common/types/Exchange';
import type { MessageModel } from '@repo/common/types/Message';
import { JsonFieldSerializer } from '@repo/toolslib/src/utils/serialize.util';
import { ValidationUtils } from '@repo/toolslib/src/utils/validation.util';
import { PrivateClientDatabaseAdapter } from '../private.client.adapter';
import type { DeduplicationStrategy } from '../types';

// Row shape the driver expects at upsert time (JSON fields serialized where appropriate)
// Keep non-JSON fields the same, and allow nulls where DB stores null
type SerializedMessageRow = Omit<MessageRow, 'data' | 'sources' | 'queryOptions'> & {
  data?: string | null;
  sources?: string | null;
  queryOptions?: string | null;
};

export class MessageClientAdapter extends PrivateClientDatabaseAdapter<
  MessageRow,
  ExchangeMessage
> {
  constructor(
    sqliteModel: MessageModel,
    userId: string,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    adapterConfig?: Partial<{
      baseUrl: string;
      authToken: string;
      timeout: number;
      retries: number;
      batchSize: number;
    }>
  ) {
    super(sqliteModel as any, 'message', userId, authToken, adapterConfig);
  }

  protected getDeduplicationStrategy(): DeduplicationStrategy<ExchangeMessage> {
    return {
      type: 'none' as const,
      getKey: (item: ExchangeMessage) => item.id
    };
  }

  /**
   * Override validateItemEquality to use the improved validateMessageEquality from ValidationUtils
   * This provides more accurate comparison for message-specific fields and data structures
   */
  protected validateItemEquality(
    item1: ExchangeMessage,
    item2: ExchangeMessage
  ): { isEqual: boolean; differences: string[] } {
    return ValidationUtils.validateMessageEquality(item1, item2);
  }

  protected async getAllFromSqlite(): Promise<MessageRow[]> {
    const rows = await (this.sqliteModel as any).findAllRows({ paranoid: true });
    // Rows from model are already objects as per toObj; still normalize to our row shape
    return rows.map((message: any) => this.mapModelMessageToRow(message));
  }

  protected async getFromSqliteById(id: string): Promise<MessageRow | null> {
    const messages = await (this.sqliteModel as any).findAllRows({
      where: { id },
      limit: 1
    });

    if (messages.length === 0) return null;

    const message = messages[0];
    return this.mapModelMessageToRow(message);
  }

  protected async upsertListToSqliteImpl(rows: MessageRow[]): Promise<void> {
    const toJSONString = (val: unknown): string | null | undefined =>
      val === undefined
        ? undefined
        : val === null
          ? null
          : typeof val === 'string'
            ? val
            : JSON.stringify(val);

    const serialized: SerializedMessageRow[] = rows.map((row) => ({
      ...row,
      data: toJSONString((row as any).data),
      sources: toJSONString((row as any).sources),
      queryOptions: toJSONString((row as any).queryOptions)
    }));

    await this.sqliteModel.bulkUpsertItemsRaw(serialized as unknown as any);
  }

  protected sqliteRowToItem(row: MessageRow): ExchangeMessage {
    const fallbackDate = new Date('1970-01-01T00:00:00.000Z');
    function safeDate(val: any): Date | undefined {
      if (!val) return fallbackDate;
      const d = new Date(val);
      return isNaN(d.getTime()) ? fallbackDate : d;
    }

    // Plain JSON parse (no Date reviver) to keep ISO timestamps as strings
    const safePlainParse = (val: any): any => {
      if (val === null || val === undefined) return undefined;
      if (typeof val !== 'string') return val;
      try {
        return JSON.parse(val);
      } catch {
        return undefined;
      }
    };

    // Use standardized JSON field deserialization with special handling for test cases
    const parsedData = JsonFieldSerializer.deserializePreserveNulls(row.data);
    const parsedSources = JsonFieldSerializer.deserializePreserveNulls(row.sources);
    // Do not convert ISO date strings in queryOptions to Date objects; keep strings
    const parsedQueryOptions = safePlainParse(row.queryOptions);

    // For data field, preserve nulls to maintain test compatibility
    // For queryOptions, normalize nulls to undefined for consistency
    const normalizedQueryOptions = JsonFieldSerializer.normalizeNulls(parsedQueryOptions);

    const baseMessage = {
      id: row.id,
      type: row.type,
      subtype: row.subtype,
      sender: row.sender as 'user' | 'assistant' | 'system',
      date: row.date && !isNaN(new Date(row.date).getTime()) ? new Date(row.date) : undefined,
      createdAt: safeDate(row.createdAt),
      updatedAt: safeDate(row.updatedAt),
      deletedAt: row.deletedAt ? safeDate(row.deletedAt) : undefined,
      transcriptId: row.transcriptId,
      conversationId: row.conversationId
    };

    // Handle different message types
    if (row.query) {
      // Query message
      return {
        ...baseMessage,
        query: row.query,
        queryOptions: normalizedQueryOptions
      } as any;
    } else if (parsedData || parsedSources) {
      // Response message - data might be objects or JSON strings
      // Extract model and contentLength fields from data if present
      let model: string | undefined;
      let contentLength: number | undefined;
      let dataWithoutExtras = parsedData;
      if (parsedData && typeof parsedData === 'object') {
        if ('model' in parsedData && typeof (parsedData as any).model === 'string') {
          model = (parsedData as any).model;
        }
        if ('contentLength' in parsedData && typeof (parsedData as any).contentLength === 'number') {
          contentLength = (parsedData as any).contentLength;
        }
        if (model !== undefined || contentLength !== undefined) {
          const { model: _, contentLength: __, ...rest } = parsedData as any;
          dataWithoutExtras = rest;
        }
      }

      return {
        ...baseMessage,
        data: dataWithoutExtras,
        sources: parsedSources,
        ...(model && { model }),
        ...(contentLength !== undefined && { contentLength })
      } as any;
    } else {
      // Basic message
      return baseMessage as any;
    }
  }

  protected validateItemForSqlite(item: ExchangeMessage): void {
    if (!item.id) {
      throw new Error('Message must have an id');
    }
    if (!(item as any).type) {
      throw new Error(`Message ${item.id} must have a type`);
    }
    if (!(item as any).sender) {
      throw new Error(`Message ${item.id} must have a sender`);
    }
    if (!(item as any).date) {
      throw new Error(`Message ${item.id} must have a date`);
    }
    if (!item.updatedAt) {
      throw new Error(`Message ${item.id} must have an updatedAt`);
    }
  }

  protected validateSqliteRow(row: MessageRow): void {
    if (!row.id) {
      throw new Error('Message SQLite row must have an id');
    }
    if (!row.type) {
      throw new Error(`Message SQLite row ${row.id} must have a type`);
    }
    if (!row.sender) {
      throw new Error(`Message SQLite row ${row.id} must have a sender`);
    }
    if (!row.date) {
      throw new Error(`Message SQLite row ${row.id} must have a date`);
    }
    if (!row.updatedAt) {
      throw new Error(`Message SQLite row ${row.id} must have an updatedAt`);
    }
  }

  protected validateItemForCloud(item: ExchangeMessage): void {
    if (!item.id) {
      throw new Error('Message must have an id');
    }
    if (!(item as any).type) {
      throw new Error(`Message ${item.id} must have a type`);
    }
    if (!(item as any).sender) {
      throw new Error(`Message ${item.id} must have a sender`);
    }
    if (!(item as any).date) {
      throw new Error(`Message ${item.id} must have a date`);
    }
    if (!item.updatedAt) {
      throw new Error(`Message ${item.id} must have an updatedAt`);
    }
  }

  protected itemToSqliteRow(item: ExchangeMessage): MessageRow {
    // Handle model and contentLength fields - merge them into data if present
    let dataField = (item as any)?.data;

    // Check for model and contentLength fields at the top level of the message
    const model = (item as any)?.model;
    const contentLength = (item as any)?.contentLength;

    if (model || contentLength !== undefined) {
      if (dataField && typeof dataField === 'object') {
        dataField = { ...dataField };
        if (model) dataField.model = model;
        if (contentLength !== undefined) dataField.contentLength = contentLength;
      } else {
        dataField = {};
        if (model) dataField.model = model;
        if (contentLength !== undefined) dataField.contentLength = contentLength;
      }
    }

    // For SQLite, return objects/arrays/null for JSON fields. Serialization happens in the SQLite model.
    return {
      id: item.id,
      type: item.type,
      subtype: (item as any)?.subtype,
      sender: item.sender,
      date: new Date((item as any)?.date || undefined).toISOString(),
      data: dataField as any,
      query: (item as any)?.query,
      queryOptions: (item as any)?.queryOptions as any,
      sources: (item as any)?.sources as any,
      createdAt: item.createdAt?.toISOString() || '1970-01-01T00:00:00.000Z',
      updatedAt: item.updatedAt?.toISOString() || '1970-01-01T00:00:00.000Z',
      deletedAt: item.deletedAt ? item.deletedAt.toISOString() : undefined,
      transcriptId: (item as any)?.transcriptId,
      conversationId: (item as any)?.conversationId
    };
  }

  private mapModelMessageToRow(message: any): MessageRow {
    const fallbackDate = '1970-01-01T00:00:00.000Z';

    const toIso = (d: any): string => (d ? new Date(d).toISOString() : fallbackDate);

    // Normalize undefined -> null for DB-aligned optional fields
    const toNull = (v: any) => (v === undefined ? null : v);

    return {
      id: message.id,
      type: message.type,
      subtype: toNull(message.subtype),
      sender: message.sender,
      date: toIso(message.date),
      // JSON-capable fields: keep object/array when present (model already parsed), else null
      data: toNull((message as any)?.data),
      query: toNull((message as any)?.query),
      queryOptions: toNull((message as any)?.queryOptions),
      sources: toNull((message as any)?.sources),
      createdAt: toIso(message.createdAt),
      updatedAt: toIso(message.updatedAt),
      deletedAt: message.deletedAt ? new Date(message.deletedAt).toISOString() : null,
      transcriptId: toNull(message.transcriptId),
      conversationId: toNull(message.conversationId)
    } as MessageRow;
  }
}
